<template>
  <div class="email-composer-container">
    <!-- Header -->
    <div class="composer-header">
      <div class="header-content">
        <div class="header-left">
          <v-btn icon @click="$router.back()" class="back-btn">
            <v-icon color="#fff">mdi-arrow-left</v-icon>
          </v-btn>
          <div class="header-title">
            <h2 class="title-text">{{ $route.query._id ? 'Edit Email' : 'Compose Email' }}</h2>
            <p class="subtitle-text">Send personalized emails to your audience</p>
          </div>
        </div>
        <div class="header-actions">
          <v-btn
            v-if="$route.query._id"
            outlined
            color="error"
            @click="onDelete"
            class="delete-btn"
          >
            <v-icon left small>mdi-delete-outline</v-icon>
            Delete
          </v-btn>
          <v-btn
            :loading="loading"
            :disabled="!canSend"
            color="primary"
            elevation="2"
            class="send-btn"
            @click="onSend"
          >
            <v-icon left small color="#fff">mdi-send</v-icon>
            {{ $route.query._id ? "Resend" : "Send Email" }}
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="composer-content">
      <div class="content-wrapper">
        <!-- Recipients Section -->
        <div class="section-card recipients-section">
          <div class="section-header">
            <v-icon class="section-icon" color="#084468">mdi-account-multiple</v-icon>
            <div class="section-title">
              <h3>Recipients</h3>
              <p class="section-subtitle">{{ toArray.length }} recipient{{ toArray.length !== 1 ? 's' : '' }} selected</p>
            </div>
          </div>
          
          <div class="recipients-input-container">
            <div class="recipients-chips" v-if="toArray.length">
              <v-chip
                v-for="chip in toArray"
                :key="chip"
                close
                small
                class="recipient-chip"
                @click:close="onCloseChip(chip)"
              >
                <!-- <v-avatar left size="20" class="chip-avatar">
                  <v-icon small>mdi-account</v-icon>
                </v-avatar> -->
                {{ chip }}
              </v-chip>
            </div>
            
            <div class="recipient-search">
              <v-menu
                v-model="toEmailPopup"
                offset-y
                max-height="400"
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="searchKey"
                    ref="toEmail"
                    placeholder="Search people to add..."
                    solo
                    flat
                    dense
                    hide-details
                    class="search-input"
                    prepend-inner-icon="mdi-magnify"
                    v-bind="attrs"
                    v-on="on"
                    autocomplete="off"
                    @keyup.enter="onSelectFirstUser"
                  />
                </template>
                
                <v-card class="recipients-dropdown">
                  <v-list dense>
                    <v-list-item
                      v-for="(item, index) in filteredUsers"
                      :key="index"
                      @click="selectUser(item)"
                      class="user-list-item"
                    >
                      <v-list-item-avatar size="32">
                        <v-img
                          :src="item.midThumbnail || item.image"
                          class="user-avatar"
                        >
                          <template v-slot:placeholder>
                            <v-avatar color="grey lighten-2" size="32">
                              <v-icon>mdi-account</v-icon>
                            </v-avatar>
                          </template>
                        </v-img>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title class="user-name">{{ item.name }}</v-list-item-title>
                        <v-list-item-subtitle class="user-email">{{ item.email }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    
                    <v-divider v-if="filteredUsers.length" />
                    
                    <v-list-item @click="onSelectAllFans" class="special-action">
                      <v-list-item-avatar>
                        <v-avatar color="primary" size="32">
                          <v-icon color="white" small>mdi-account-group</v-icon>
                        </v-avatar>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title>Select All Fanned Volunteers</v-list-item-title>
                        <v-list-item-subtitle>Add all volunteers at once</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-menu>
            </div>
          </div>
        </div>

        <!-- Subject Section -->
        <div class="section-card subject-section">
          <div class="section-header">
            <v-icon class="section-icon" color="#084468">mdi-format-title</v-icon>
            <div class="section-title">
              <h3>Subject</h3>
              <p class="section-subtitle">Make it engaging and clear</p>
            </div>
          </div>
          
          <v-text-field
            v-model="subject"
            placeholder="Enter email subject..."
            solo
            flat
            hide-details
            class="subject-input"
            @input="onChange"
          />
        </div>

        <!-- Content Section -->
        <div class="section-card content-section">
          <div class="section-header">
            <v-icon class="section-icon" color="#084468">mdi-text-box</v-icon>
            <div class="section-title">
              <h3>Message</h3>
              <p class="section-subtitle">Craft your message with rich formatting</p>
            </div>
          </div>
          
          <div class="editor-container">
            <vue-editor
              :editorOptions="editorSettings"
              v-model="emailContent"
              useCustomImageHandler
              @image-added="imageHandler"
              class="custom-editor"
            />
          </div>
        </div>

        <!-- Error Messages -->
        <v-alert
          v-if="errorMessage"

          dense
          dismissible
          class="error-alert"
          @input="errorMessage = null"
        >
          {{ errorMessage }}
        </v-alert>
      </div>
    </div>

    <!-- Floating Action Button for Mobile -->
    <v-btn
      v-if="$vuetify.breakpoint.mobile"
      fab
      fixed
      bottom
      right
      color="primary"
      class="mobile-send-fab"
      :disabled="!canSend"
      :loading="loading"
      @click="onSend"
    >
      <v-icon>mdi-send</v-icon>
    </v-btn>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :timeout="4000"
      bottom
      color="success"
    >
      {{ snackMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400" persistent>
      <v-card class="delete-dialog">
        <v-card-title class="headline">
          <v-icon color="error" class="mr-2">mdi-delete-alert</v-icon>
          Delete Email
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete this email? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="onCloseDialog">Cancel</v-btn>
          <v-btn
            color="error"
            :loading="isDeleting"
            @click="onConfirmDelete"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Quill from "quill";

window.Quill = Quill;
const ImageResize = require("quill-image-resize-module").default;
Quill.register("modules/imageResize", ImageResize);
const VideoResize = require("quill-video-resize-module").default;
Quill.register("modules/VideoResize", VideoResize);

export default {
  components: {  },
  data() {
    return {
      fromEmail: "info@akindercompany.com",
      subject: "",
      emailContent: "",
      editorSettings: {
        modules: {
          imageResize: {},
          VideoResize: {},
          toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['link', 'image', 'video'],
            ['clean']
          ]
        },
        theme: 'snow',
        placeholder: 'Start typing your message...'
      },
      toEmailPopup: false,
      searchKey: null,
      filteredUsers: [],
      toArray: [],
      loading: false,
      deleteDialog: false,
      isDeleting: false,
      snackbar: false,
      snackMessage: null,
      errorMessage: null
    };
  },
  computed: {
    ...mapGetters("auth", {
      profile: "getProfile"
    }),
    canSend() {
      return this.fromEmail && this.toArray.length && this.subject && this.emailContent;
    }
  },
  methods: {
    ...mapActions({
      uploadBlogImage: "blog/uploadBlogImage",
      sendEmail: "agency/sendAgencyEmail",
      searchUsers: "chat/searchVolunteersEmail",
      getMessageLog: "messagelog/getLogDetails",
      deleteMessageLog: "messagelog/deleteLog"
    }),
    onChange() {
      this.errorMessage = null;
    },
    onSend() {
      this.errorMessage = null;
      if (!this.canSend) {
        this.errorMessage = "Please fill in all required fields";
        return;
      }

      this.loading = true;
      this.sendEmail({
        from: this.fromEmail,
        toArray: this.toArray,
        subject: this.subject,
        content: this.emailContent
      })
        .then(message => {
          this.loading = false;
          this.toArray = [];
          this.subject = "";
          this.emailContent = "";
          this.showSnackMessage(message || "Email sent successfully!");
          if (this.$route.query._id) {
            this.$router.back();
          }
        })
        .catch(error => {
          this.loading = false;
          this.errorMessage = error.response?.data?.message || "Failed to send email";
        });
    },
    imageHandler(file, Editor, cursorLocation, resetUploader) {
      const formData = new FormData();
      formData.append("file", file);
      
      this.uploadBlogImage(formData)
        .then(res => {
          Editor.insertEmbed(cursorLocation, "image", res);
          resetUploader();
        })
        .catch(error => {
          console.error("Image upload failed:", error);
          this.errorMessage = "Failed to upload image";
        });
    },
    onSelectAllFans() {
      if (!this.toArray.includes("All Fans")) {
        this.toArray.push("All Fans");
      }
      this.searchKey = "";
      this.toEmailPopup = false;
      this.$refs.toEmail.focus();
    },
    showSnackMessage(message) {
      this.snackbar = true;
      this.snackMessage = message;
    },
    onCloseChip(chip) {
      this.toArray = this.toArray.filter(t => t !== chip);
    },
    onSelectFirstUser() {
      if (this.filteredUsers.length && !this.toArray.includes(this.filteredUsers[0].email)) {
        this.toArray.push(this.filteredUsers[0].email);
      }
      this.searchKey = "";
      this.toEmailPopup = false;
    },
    selectUser(item) {
      if (!this.toArray.includes(item.email)) {
        this.toArray.push(item.email);
      }
      this.searchKey = "";
      this.toEmailPopup = false;
      this.$refs.toEmail.focus();
    },
    onDelete() {
      this.deleteDialog = true;
    },
    onCloseDialog() {
      this.deleteDialog = false;
    },
    onConfirmDelete() {
      this.isDeleting = true;
      this.deleteMessageLog({ _id: this.$route.query._id })
        .then(() => {
          this.$router.back();
        })
        .catch(error => {
          this.isDeleting = false;
          this.errorMessage = error.response?.data?.message || "Failed to delete email";
        });
    }
  },
  watch: {
    searchKey(newValue) {
      if (newValue && newValue.length > 1) {
        this.searchUsers(newValue)
          .then(res => {
            this.filteredUsers = res || [];
          })
          .catch(error => {
            console.error("Search failed:", error);
            this.filteredUsers = [];
          });
      } else {
        this.filteredUsers = [];
      }
    }
  },
  mounted() {
    if (this.$route.query._id) {
      this.loading = true;
      this.getMessageLog({ _id: this.$route.query._id })
        .then(res => {
          this.loading = false;
          this.fromEmail = res.from;
          this.toArray = res.to || [];
          this.subject = res.subject || "";
          this.emailContent = res.content || "";
        })
        .catch(error => {
          console.error("Failed to load email:", error);
          this.loading = false;
          this.errorMessage = "Failed to load email data";
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.email-composer-container {
  min-height: 100vh;
  // background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  
  .composer-header {
background: linear-gradient(135deg, #084468, #3b7ca1);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    position: sticky;
    top: 0;
    z-index: 10;
    border-bottom-left-radius: 22px;
    border-bottom-right-radius: 22px;
    color: #fff;
    
    .header-content {
      max-width: 1200px;
      margin: 0 auto;
      padding: 20px 24px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      
      .header-left {
        display: flex;
        align-items: center;
        gap: 16px;
        
        .back-btn {
          background: rgba(103, 126, 234, 0.1);
          border: 1px solid #ffff;
          
          &:hover {
            background: rgba(103, 126, 234, 0.2);
          }
        }
        
        .header-title {
          .title-text {
            margin: 0;
            font-size: 24px;
            font-weight: 600;
            color: #ffff;
          }
          
          .subtitle-text {
            margin: 4px 0 0 0;
            font-size: 14px;
            color: #ffff;
          }
        }
      }
      
      .header-actions {
        display: flex;
        gap: 12px;
        
        .delete-btn {
          border-color: #e74c3c;
          color: #e74c3c;
          
          &:hover {
            background: rgba(231, 76, 60, 0.1);
          }
        }
        
        .send-btn {
          background: linear-gradient(135deg,#3b7ca1,#084468);
          box-shadow: 0 4px 15px rgba(103, 126, 234, 0.4);
          color: #fff !important;
          border: 1px solid #ffff;
          &:hover {
            box-shadow: 0 6px 20px rgba(103, 126, 234, 0.6);
            transform: translateY(-1px);
            color: #ffff;
          }
        }
      }
    }
  }
  
  .composer-content {
    padding: 32px 24px;
    
    .content-wrapper {
      max-width: 1200px;
      margin: 0 auto;
      display: flex;
      flex-direction: column;
      gap: 24px;
    }
    
    .section-card {
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(10px);
      border-radius: 16px;
      padding: 24px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      transition: all 0.3s ease;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
      }
      
      .section-header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 20px;
        
        .section-icon {
          background: rgba(103, 126, 234, 0.1);
          padding: 8px;
          border-radius: 8px;
        }
        
        .section-title {
          h3 {
            margin: 0;
            font-size: 18px;
            font-weight: 600;
            color: #2c3e50;
          }
          
          .section-subtitle {
            margin: 2px 0 0 0;
            font-size: 13px;
            color: #7f8c8d;
          }
        }
      }
    }
    
    .recipients-section {
      .recipients-input-container {
        .recipients-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 16px;
          
          .recipient-chip {
            background: linear-gradient(135deg,#3b7ca1,#084468);
            color: white;
            
            .chip-avatar {
              background: rgba(255, 255, 255, 0.2);
            }
          }
        }
        
        .recipient-search {
          .search-input {
            background: #f8f9fa;
            border-radius: 12px;
            
            ::v-deep .v-input__slot {
              box-shadow: none !important;
            }
          }
        }
        
        .recipients-dropdown {
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
          
          .user-list-item {
            border-radius: 8px;
            margin: 4px 8px;
            
            &:hover {
              background: rgba(103, 126, 234, 0.1);
            }
            
            .user-avatar {
              border-radius: 50%;
            }
            
            .user-name {
              font-weight: 500;
              color: #2c3e50;
            }
            
            .user-email {
              color: #7f8c8d;
              font-size: 12px;
            }
          }
          
          .special-action {
            background: rgba(103, 126, 234, 0.05);
            border-radius: 8px;
            margin: 8px;
            
            &:hover {
              background: rgba(103, 126, 234, 0.1);
            }
          }
        }
      }
    }
    
    .subject-section {
      .subject-input {
        background: #f8f9fa;
        border-radius: 12px;
        
        ::v-deep .v-input__slot {
          box-shadow: none !important;
        }
      }
    }
    
    .content-section {
      .editor-container {
        border-radius: 12px;
        overflow: hidden;
        border: 1px solid #e9ecef;
        
        ::v-deep .custom-editor {
          .quillWrapper {
            .ql-toolbar {
              background: #f8f9fa;
              border-bottom: 1px solid #e9ecef;
            }
            
            .ql-container {
              min-height: 300px;
              
              .ql-editor {
                font-size: 14px;
                line-height: 1.6;
                
                &.ql-blank::before {
                  color: #adb5bd;
                  font-style: normal;
                }
              }
            }
          }
        }
      }
    }
    
    .error-alert {
      border-radius: 12px;
    }
  }
  
  .mobile-send-fab {
    background: linear-gradient(135deg,#3b7ca1,#084468);
    box-shadow: 0 4px 20px rgba(103, 126, 234, 0.4);
  }
  
  .delete-dialog {
    border-radius: 16px;
    
    .headline {
      background: #f8f9fa;
      margin: -24px -24px 16px -24px;
      padding: 20px 24px;
      border-radius: 16px 16px 0 0;
    }
  }
}

// Responsive Design
@media (max-width: 768px) {
  .email-composer-container {
    .composer-header {
      .header-content {
        flex-direction: column;
        gap: 16px;
        text-align: center;
        
        .header-left {
          justify-content: center;
        }
        
        .header-actions {
          .send-btn {
            display: none; // Hide on mobile, use FAB instead
          }
        }
      }
    }
    
    .composer-content {
      padding: 16px;
      
      .section-card {
        padding: 16px;
        
        .section-header {
          .section-title {
            h3 {
              font-size: 16px;
            }
          }
        }
      }
      
      .recipients-section {
        .recipients-input-container {
          .recipients-chips {
            .recipient-chip {
              font-size: 12px;
            }
          }
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .email-composer-container {
    .composer-header {
      .header-content {
        padding: 16px;
        
        .header-left {
          .header-title {
            .title-text {
              font-size: 20px;
            }
          }
        }
      }
    }
  }
}
</style>